import { useMemo } from "react"
import { useQuery } from "urql"

import { PRICE_CHANGE_PAGE_SIZE, PriceChangeTableVariant } from "../constants"

import {
  GetPriceChangeItemsDocument,
  type GetPriceChangeItemsInput,
  type GetPriceChangeItemsQuery,
  type PriceChangeItems,
} from "@/graphql/codegen/graphql"
import { getPriceChangeItemsNormalizer } from "@/screens/Pricing/utils/normalize"

import "../graphql/queries"

interface UseGetPriceChangeItemsReturn {
  priceChangeItems: PriceChangeItems[] | null
  total: number
  isFetching: boolean
  pageCount: number
  refetch: () => void
}

export const useGetPriceChangeItems = (
  input: GetPriceChangeItemsInput | null,
  page: number
): UseGetPriceChangeItemsReturn => {
  const [{ data, fetching }, reexecuteQuery] = useQuery<GetPriceChangeItemsQuery>({
    query: GetPriceChangeItemsDocument,
    variables: { input: { ...input!, offset: (page - 1) * PRICE_CHANGE_PAGE_SIZE, limit: PRICE_CHANGE_PAGE_SIZE } },
    pause: !input,
  })

  const refetch = () => reexecuteQuery({ requestPolicy: "network-only" })

  const { items: fetched, total: fetchedTotal } = useMemo(() => {
    if (!input) return { items: null, total: 0 }

    if (data?.priceChange.getPriceChangeItems.__typename === "GetPriceChangeItemsSuccess") {
      const success = data.priceChange.getPriceChangeItems
      const normalize = getPriceChangeItemsNormalizer(PriceChangeTableVariant.SubmitPriceChange)
      return {
        items: normalize(success.priceChangeItems as PriceChangeItems[]),
        total: success.pageInfo.total,
      }
    }
    return { items: [] as PriceChangeItems[], total: 0 }
  }, [data, input])

  return {
    priceChangeItems: fetched,
    total: fetchedTotal,
    isFetching: fetching,
    pageCount: Math.ceil(fetchedTotal / PRICE_CHANGE_PAGE_SIZE),
    refetch,
  }
}
